import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Link,
  Grid,
  CircularProgress,
  Avatar
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';

const NewsSentiment = ({ data, loading }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Handle symbol chip click
  const handleSymbolClick = (symbol) => {
    navigate(`/asset/${symbol}`);
  };

  // Helper function to get sentiment color and icon
  const getSentimentDisplay = (sentiment, sentimentScore) => {
    const normalizedSentiment = sentiment?.toLowerCase();

    if (normalizedSentiment === 'positive') {
      return {
        color: theme.palette.positive.main,
        borderColor: theme.palette.positive.main,
        icon: <TrendingUpIcon fontSize="small" />,
        label: 'Positive'
      };
    } else if (normalizedSentiment === 'negative') {
      return {
        color: theme.palette.negative.main,
        borderColor: theme.palette.negative.main,
        icon: <TrendingDownIcon fontSize="small" />,
        label: 'Negative'
      };
    } else {
      return {
        color: theme.palette.lightGray.main,
        borderColor: theme.palette.lightGray.main,
        icon: <TrendingFlatIcon fontSize="small" />,
        label: 'Neutral'
      };
    }
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper function to truncate text
  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Helper function to get site favicon or create letter avatar
  const getSiteDisplay = (site) => {
    if (!site) return null;

    // Create a simple letter avatar from site name
    const firstLetter = site.charAt(0).toUpperCase();

    return (
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: '0.875rem',
          bgcolor: theme.palette.primary.main,
          color: 'white'
        }}
      >
        {firstLetter}
      </Avatar>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* News Cards Grid */}
      <Grid container spacing={3}>
        {data && data.length > 0 ? (
          data.map((article, index) => {
            const sentimentDisplay = getSentimentDisplay(article.sentiment, article.sentimentScore);

            return (
              <Grid item xs={12} md={6} lg={4} key={`${article.symbol}-${index}`}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease-in-out',
                    borderLeft: `4px solid ${sentimentDisplay.borderColor}`,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[8],
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    {/* Header with Symbol and Sentiment */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      {article.symbol ? (
                        <Chip
                          label={article.symbol}
                          variant="outlined"
                          size="small"
                          clickable
                          onClick={() => handleSymbolClick(article.symbol)}
                          sx={{
                            fontWeight: 'bold',
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: theme.palette.primary.main,
                              color: 'white',
                              transform: 'scale(1.05)',
                            }
                          }}
                        />
                      ) : article.publisher ? (
                        <Chip
                          label={article.publisher}
                          variant="outlined"
                          size="small"
                          sx={{
                            fontWeight: 'bold',
                            borderColor: theme.palette.text.secondary,
                            color: theme.palette.text.secondary,
                          }}
                        />
                      ) : null}
                      {article.sentiment && (
                        <Chip
                          icon={sentimentDisplay.icon}
                          label={sentimentDisplay.label}
                          size="small"
                          sx={{
                            backgroundColor: `${sentimentDisplay.color}20`,
                            color: sentimentDisplay.color,
                            border: `1px solid ${sentimentDisplay.color}`,
                            fontWeight: 'bold'
                          }}
                        />
                      )}
                    </Box>

                    {/* Article Title */}
                    <Typography
                      variant="h6"
                      component="h3"
                      gutterBottom
                      sx={{
                        fontWeight: 'bold',
                        lineHeight: 1.3,
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {article.title}
                    </Typography>

                    {/* Article Text Preview */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        lineHeight: 1.4
                      }}
                    >
                      {truncateText(article.text)}
                    </Typography>

                    {/* Footer with Source and Date */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mt="auto"
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        {getSiteDisplay(article.site)}
                        <Typography variant="caption" color="text.secondary">
                          {article.site}
                        </Typography>
                      </Box>

                      <Typography variant="caption" color="text.secondary">
                        {formatDate(article.publishedDate)}
                      </Typography>
                    </Box>

                    {/* Sentiment Score */}
                    {article.sentiment && article.sentimentScore !== undefined && (
                      <Box mt={2}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          Sentiment Score:
                          <span style={{
                            color: sentimentDisplay.color,
                            fontWeight: 'bold',
                            marginLeft: '4px'
                          }}>
                            {article.sentimentScore.toFixed(3)}
                          </span>
                        </Typography>
                      </Box>
                    )}

                    {/* Read More Link */}
                    {article.url && (
                      <Box mt={2}>
                        <Link
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: theme.palette.primary.main,
                            textDecoration: 'none',
                            fontWeight: 'bold',
                            fontSize: '0.875rem',
                            '&:hover': {
                              textDecoration: 'underline',
                            }
                          }}
                        >
                          Read Full Article →
                        </Link>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })
        ) : (
          <Grid item xs={12}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={8}
            >
              <Typography variant="h6" color="text.secondary">
                No news articles available
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default NewsSentiment;